import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
	AdministratorSettingKey,
	AdministratorSettingsPayload,
	StaffAccessEvent,
	StaffAccessPayload,
	StaffBootstrap,
	StaffRole,
	StaffUserRecord,
} from '../shared/types';
import { FIRM_NAME } from '../shared/firm';
import { NameInput } from './NameInput';
import { api, ApiError } from './api';
import { revealFeedback } from './feedback';
import { friendlyStaffError } from './staffFeedback';
import { visibleAccessHistory } from './accessHistoryVisibility';
import { staffIdentityLabel } from './staffIdentity';

const staffRoles: StaffRole[] = ['administrator', 'attorney', 'intake_staff', 'read_only'];
const administratorSettingKeys = ['intake_creation_enabled', 'client_access_enabled', 'outbound_delivery_enabled'] as const;
const administratorSettingDefinitions: Record<AdministratorSettingKey, { label: string; description: string; confirmation: string }> = {
	intake_creation_enabled: {
		label: 'Intake creation',
		description: 'Controls creation of new intake records. Existing records remain available.',
		confirmation: 'This affects new intake creation only.',
	},
	client_access_enabled: {
		label: 'Client access',
		description: 'Controls client link redemption and authenticated questionnaire access. Staff access remains available.',
		confirmation: 'Clients will be unable to open or save questionnaires while this is disabled.',
	},
	outbound_delivery_enabled: {
		label: 'Outbound delivery',
		description: 'Controls external provider delivery after all runtime safety checks.',
		confirmation: 'Enable this only after the technical operator confirms that provider delivery is active.',
	},
};

export function StaffAdministrationApp() {
	const [staff, setStaff] = useState<StaffBootstrap | null>(null);
	const [settings, setSettings] = useState<AdministratorSettingsPayload | null>(null);
	const [access, setAccess] = useState<StaffAccessPayload | null>(null);
	const [message, setMessage] = useState('Signing in...');
	const [messageKind, setMessageKind] = useState<'status' | 'error' | 'confirmation'>('status');
	const [settingInProgress, setSettingInProgress] = useState<AdministratorSettingKey | null>(null);
	const [staffChangeInProgress, setStaffChangeInProgress] = useState<string | null>(null);
	const messageRef = useRef<HTMLParagraphElement>(null);

	function showMessage(text: string, kind: 'status' | 'error' | 'confirmation' = 'status') {
		setMessageKind(kind);
		setMessage(text);
	}

	useEffect(() => {
		void api<StaffBootstrap>('/api/staff/bootstrap')
			.then(async (payload) => {
				setStaff(payload);
				if (payload.role !== 'administrator') {
					showMessage('Administrator access is required.', 'error');
					return;
				}
				await Promise.all([loadSettings(), loadAccess()]);
				showMessage('');
			})
			.catch((error: unknown) => showMessage(friendlyStaffError(error, 'Unable to sign in'), 'error'));
	}, []);

	useEffect(() => {
		if (messageKind === 'confirmation' || messageKind === 'status') revealFeedback(messageRef.current);
	}, [message, messageKind]);

	async function loadSettings() {
		setSettings(await api<AdministratorSettingsPayload>('/api/staff/settings'));
	}

	async function loadAccess() {
		setAccess(await api<StaffAccessPayload>('/api/staff/access'));
	}

	async function changeSetting(key: AdministratorSettingKey, enabled: boolean) {
		if (!staff || staff.role !== 'administrator' || !settings) return;
		const definition = administratorSettingDefinitions[key];
		const action = enabled ? 'Enable' : 'Disable';
		if (!window.confirm(`${action} ${definition.label.toLowerCase()}? ${definition.confirmation}`)) return;
		setSettingInProgress(key);
		try {
			const result = await api<{ key: AdministratorSettingKey; enabled: boolean }>(
				`/api/staff/settings/${key}`,
				{ method: 'PUT', body: JSON.stringify({ enabled }) },
				staff.csrfToken,
			);
			setSettings((current) => (current ? { ...current, settings: { ...current.settings, [result.key]: result.enabled } } : current));
			showMessage(`${definition.label} ${result.enabled ? 'enabled' : 'disabled'}.`, 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Administrator setting was not changed'), 'error');
		} finally {
			setSettingInProgress(null);
		}
	}

	async function addStaff(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!staff || staff.role !== 'administrator') return;
		const form = event.currentTarget;
		const data = new FormData(form);
		const email = String(data.get('email') ?? '')
			.trim()
			.toLowerCase();
		setStaffChangeInProgress(email || 'new');
		try {
			await api(
				'/api/staff/access/users',
				{
					method: 'POST',
					body: JSON.stringify({
						email,
						displayName: String(data.get('displayName') ?? '').trim(),
						role: String(data.get('role') ?? 'read_only'),
					}),
				},
				staff.csrfToken,
			);
			form.reset();
			await loadAccess();
			showMessage(`Staff access created for ${email}.`, 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Staff access was not created'), 'error');
		} finally {
			setStaffChangeInProgress(null);
		}
	}

	async function updateStaff(input: { email: string; displayName?: string; role?: StaffRole; enabled?: boolean }) {
		if (!staff || staff.role !== 'administrator') return;
		const statusAction = input.enabled === undefined ? 'save changes for' : input.enabled ? 'restore access for' : 'disable access for';
		if (!window.confirm(`Confirm that you want to ${statusAction} ${input.email}.`)) return;
		setStaffChangeInProgress(input.email);
		try {
			await api('/api/staff/access/users', { method: 'PATCH', body: JSON.stringify(input) }, staff.csrfToken);
			await loadAccess();
			showMessage(`Staff access updated for ${input.email}.`, 'confirmation');
		} catch (error) {
			if (error instanceof ApiError && error.status === 409 && error.message === 'No staff-access changes were made.') {
				showMessage('No changes to save. These settings are already saved.', 'status');
			} else {
				showMessage(friendlyStaffError(error, 'Staff access was not changed'), 'error');
			}
		} finally {
			setStaffChangeInProgress(null);
		}
	}

	if (!staff)
		return (
			<main id="main" className="centered-panel">
				<h1>{FIRM_NAME} Administration</h1>
				<p role="status">{message}</p>
			</main>
		);

	return (
		<>
			<header className="app-header staff-header">
				<div>
					<span className="eyebrow">{FIRM_NAME}</span>
					<h1>Administration</h1>
					<p>{staffIdentityLabel(staff)}</p>
				</div>
				<a className="button-link header-link" href="/staff">
					Back to intakes
				</a>
			</header>
			<main id="main" className="administration-shell">
				{message && (
					<p
						ref={messageRef}
						className={`staff-notice staff-notice-${messageKind}`}
						role={messageKind === 'error' ? 'alert' : 'status'}
						aria-live={messageKind === 'error' ? 'assertive' : 'polite'}
						aria-atomic="true"
						tabIndex={messageKind !== 'error' ? -1 : undefined}
					>
						{message}
					</p>
				)}
				{staff.role === 'administrator' ? (
					<div className="administration-grid">
						<SystemControls payload={settings} settingInProgress={settingInProgress} onChange={changeSetting} onRefresh={loadSettings} />
						<StaffAccessPanel
							currentEmail={staff.email}
							payload={access}
							inProgress={staffChangeInProgress}
							onAdd={addStaff}
							onUpdate={updateStaff}
							onRefresh={loadAccess}
						/>
					</div>
				) : (
					<section className="panel">
						<h2>Access denied</h2>
						<p>Only administrators can view or change system controls and staff access.</p>
					</section>
				)}
			</main>
		</>
	);
}

function SystemControls({
	payload,
	settingInProgress,
	onChange,
	onRefresh,
}: {
	payload: AdministratorSettingsPayload | null;
	settingInProgress: AdministratorSettingKey | null;
	onChange: (key: AdministratorSettingKey, enabled: boolean) => Promise<void>;
	onRefresh: () => Promise<void>;
}) {
	const deliverySafeguard = payload ? deliverySafeguardNotice(payload) : null;
	return (
		<section className="panel administrator-settings" aria-labelledby="system-controls-heading">
			<div className="panel-heading">
				<div>
					<h2 id="system-controls-heading">System controls</h2>
					<p>These controls affect the entire intake system.</p>
				</div>
				<button type="button" className="text-button" disabled={settingInProgress !== null} onClick={() => void onRefresh()}>
					Refresh
				</button>
			</div>
			{payload ? (
				<>
					{deliverySafeguard && (
						<div className="delivery-safeguard-warning" role="status">
							<strong>{deliverySafeguard.heading}</strong>
							<p>{deliverySafeguard.detail}</p>
						</div>
					)}
					<div className="administrator-switch-list">
						{administratorSettingKeys.map((key) => {
							const definition = administratorSettingDefinitions[key];
							const enabled = payload.settings[key];
							return (
								<div className="administrator-switch" key={key}>
									<div>
										<strong>{definition.label}</strong>
										<p id={`${key}-description`}>{definition.description}</p>
									</div>
									<label className="switch-control">
										<input
											type="checkbox"
											checked={enabled}
											disabled={settingInProgress !== null}
											aria-describedby={`${key}-description`}
											onChange={(event) => void onChange(key, event.target.checked)}
										/>
										<span>{settingInProgress === key ? 'Changing…' : enabled ? 'Enabled' : 'Disabled'}</span>
									</label>
								</div>
							);
						})}
					</div>
				</>
			) : (
				<p>Loading system controls…</p>
			)}
		</section>
	);
}

export function deliverySafeguardNotice(payload: AdministratorSettingsPayload): { heading: string; detail: string } | null {
	if (payload.deliveryMode === 'provider') return null;
	if (payload.settings.outbound_delivery_enabled) {
		return {
			heading: 'Delivery configuration needs attention.',
			detail:
				'The outbound-delivery switch is enabled, but the deployment safeguard is still blocking provider calls. Messages will be recorded but not sent.',
		};
	}
	return {
		heading: 'External delivery is safely paused.',
		detail: 'Messages are recorded but not sent while the deployment safeguard remains in record-only mode.',
	};
}

function StaffAccessPanel({
	currentEmail,
	payload,
	inProgress,
	onAdd,
	onUpdate,
	onRefresh,
}: {
	currentEmail: string;
	payload: StaffAccessPayload | null;
	inProgress: string | null;
	onAdd: (event: FormEvent<HTMLFormElement>) => Promise<void>;
	onUpdate: (input: { email: string; displayName?: string; role?: StaffRole; enabled?: boolean }) => Promise<void>;
	onRefresh: () => Promise<void>;
}) {
	return (
		<section className="panel staff-access-panel" aria-labelledby="staff-access-heading">
			<div className="panel-heading">
				<div>
					<h2 id="staff-access-heading">Staff access</h2>
					<p>Add staff, assign application roles, or disable access.</p>
				</div>
				<button type="button" className="text-button" disabled={inProgress !== null} onClick={() => void onRefresh()}>
					Refresh
				</button>
			</div>
			<div className="access-prerequisite">
				<strong>Before adding someone here</strong>
				<p>
					The person must sign in with a Cloudflare account using their @bzlawgroup.com email address. Add them here to grant an
					intake-system role; no separate Cloudflare account invitation or policy change is required.
				</p>
			</div>
			<form className="compact-form add-staff-form" onSubmit={(event) => void onAdd(event)}>
				<h3>Add staff member</h3>
				<label>
					Name
					<NameInput name="displayName" required maxLength={200} autoComplete="name" />
				</label>
				<label>
					Email
					<input name="email" type="email" required maxLength={320} autoComplete="email" />
				</label>
				<label>
					Role
					<select name="role" defaultValue="read_only">
						{staffRoles.map((role) => (
							<option key={role} value={role}>
								{roleLabel(role)}
							</option>
						))}
					</select>
				</label>
				<button type="submit" disabled={inProgress !== null}>
					{inProgress === 'new' ? 'Adding…' : 'Add staff member'}
				</button>
			</form>
			<h3>Current staff</h3>
			{payload ? (
				<div className="staff-user-list">
					{payload.users.map((user) => (
						<StaffUserEditor
							key={`${user.email}-${user.updatedAt}`}
							user={user}
							isCurrent={user.email.toLowerCase() === currentEmail.toLowerCase()}
							busy={inProgress !== null}
							onUpdate={onUpdate}
						/>
					))}
				</div>
			) : (
				<p>Loading staff access…</p>
			)}
			<AccessHistory events={payload?.events ?? []} />
		</section>
	);
}

function StaffUserEditor({
	user,
	isCurrent,
	busy,
	onUpdate,
}: {
	user: StaffUserRecord;
	isCurrent: boolean;
	busy: boolean;
	onUpdate: (input: { email: string; displayName?: string; role?: StaffRole; enabled?: boolean }) => Promise<void>;
}) {
	const [displayName, setDisplayName] = useState(user.displayName ?? '');
	const [role, setRole] = useState<StaffRole>(user.role);
	return (
		<article className={`staff-user-card ${user.enabled ? '' : 'disabled'}`}>
			<div className="staff-user-heading">
				<div>
					<strong>{user.email}</strong>
					{isCurrent && <span className="current-user-label">You</span>}
				</div>
				<span className={`access-status ${user.enabled ? 'enabled' : 'disabled'}`}>{user.enabled ? 'Enabled' : 'Disabled'}</span>
			</div>
			<form
				className="staff-user-form"
				onSubmit={(event) => {
					event.preventDefault();
					void onUpdate({ email: user.email, displayName: displayName.trim(), role });
				}}
			>
				<label>
					Name
					<NameInput value={displayName} onValueChange={setDisplayName} required maxLength={200} />
				</label>
				<label>
					Role
					<select value={role} onChange={(event) => setRole(event.target.value as StaffRole)} disabled={isCurrent}>
						{staffRoles.map((option) => (
							<option key={option} value={option}>
								{roleLabel(option)}
							</option>
						))}
					</select>
				</label>
				<div className="button-row">
					<button type="submit" disabled={busy || !displayName.trim()}>
						Save changes
					</button>
					<button
						type="button"
						className={user.enabled ? 'danger-button' : 'secondary'}
						disabled={busy || isCurrent}
						onClick={() => void onUpdate({ email: user.email, enabled: !user.enabled })}
					>
						{user.enabled ? 'Disable access' : 'Restore access'}
					</button>
				</div>
				{isCurrent && <small>Your own role and access status cannot be changed from this screen.</small>}
			</form>
		</article>
	);
}

export function AccessHistory({ events }: { events: StaffAccessEvent[] }) {
	const displayedEvents = visibleAccessHistory(events);
	return (
		<details className="access-history">
			<summary>Access-change history ({displayedEvents.length})</summary>
			{displayedEvents.length ? (
				<ul>
					{displayedEvents.map((event) => (
						<li key={event.id}>
							<strong>{event.targetEmail}</strong> — {describeAccessEvent(event)}
							<br />
							<small>
								{new Date(event.createdAt).toLocaleString()} · changed by {event.actorEmail}
							</small>
						</li>
					))}
				</ul>
			) : (
				<p>No staff-access changes to display.</p>
			)}
		</details>
	);
}

function describeAccessEvent(event: StaffAccessEvent): string {
	if (event.action === 'created') return `access created as ${roleLabel(event.newRole)}`;
	const changes: string[] = [];
	if (event.previousDisplayName !== event.newDisplayName) changes.push('name updated');
	if (event.previousRole !== event.newRole)
		changes.push(`role changed from ${roleLabel(event.previousRole!)} to ${roleLabel(event.newRole)}`);
	if (event.previousEnabled !== event.newEnabled) changes.push(event.newEnabled ? 'access restored' : 'access disabled');
	return changes.join('; ') || 'record updated';
}

function roleLabel(role: StaffRole): string {
	return role.replaceAll('_', ' ').replace(/\b\w/gu, (letter) => letter.toUpperCase());
}
